import React, { useContext, useState } from 'react';
import HomePageContext from 'src/context/HomePageContext';
import BoxType1 from './BoxType1';
import BoxType2 from './BoxType2';
import { PiCertificateFill } from 'react-icons/pi';
import { FaFileDownload, FaFile, FaArrowAltCircleDown, FaEye } from 'react-icons/fa';
import { RiCheckboxMultipleBlankFill, RiCheckboxMultipleFill } from 'react-icons/ri';
import { PiCertificateBold } from 'react-icons/pi';
import { Button, Space, notification, Modal, Tooltip } from 'antd';
import ComplianceItem from './ComplianceItem';
import { getWatermarkedFile } from 'src/api/microsoft';
import PropTypes from 'prop-types';
import { notArrayOrEmpty, notEmptyArray } from 'src/misc/Misc';
import { CloseOutlined } from '@ant-design/icons';
import DocumentsCheckboxGroup from './DocumentsCheckboxGroup';

const DownloadButtonComponent = ({ children, type, onClick }) => {
  const { isDownloading, setIsDownloading } = useContext(HomePageContext);
  return (
    <Button
      type={type}
      loading={isDownloading}
      onClick={async () => {
        setIsDownloading(true);
        await onClick();
        setIsDownloading(false);
      }}
      size='large'
    >
      {children}
    </Button>
  );
};

DownloadButtonComponent.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  onClick: PropTypes.func,
};

const SectionCompliance = () => {
  const ICON_SIZE = '64px';
  const { openDrawer } = useContext(HomePageContext);

  const title = (
    <Space className='btn'>
      <PiCertificateFill />
      <div>
        Compliance<span className='font-thin opacity-60'> | downloads</span>
      </div>
    </Space>
  );

  const itemList = [
    {
      id: '1',
      image: '/images/logos/compliance/STAR-Level-1-badge-f572686709b8f897f07df08ad760e0fe05f6fd523aff965a5b283e19d60247fa.svg',
      title: 'CSA',
      desc: <>Founded in 2013 by the Cloud Security Alliance, the Security Trust Assurance and Risk (STAR) registry encompasses key principles of transparency, rigorous auditing, and cloud security and privacy best practices.</>,
      url: 'https://cloudsecurityalliance.org/star/registry/genesys-cloud-services/services/genesys-cloud-cx',
      cta: (
        <Space className='btn'>
          <FaEye />
          View STAR Registry
        </Space>
      ),
    },
    {
      id: '2',
      image: '/images/logos/compliance/bsi_c5.png',
      title: 'C5',
      desc: <>The cloud computing compliance criteria catalogue (C5) defines a baseline security level for cloud computing. It’s used by professional cloud service providers, auditors and cloud customers.</>,
      url: 'https://www.genesys.com/trust/c5',
      files: [
        {
          title: 'Type 2 SOC 2 + C5 - 2023',
          name: 'Type 2 SOC 2 + C5 - 2023.pdf',
          desc: (
            <>
              <h3>Type 2 SOC 2 With Cloud Computing Compliance Controls Catalogue (C5) & ISAE 3000 - 2023</h3>
              <p>Developed by the American Institute of CPAs (AICPA), SOC 2 defines criteria for managing customer data based on the 5 "trust service principles" . Genesys SOC scope covers only 3 (Security, Availability and Confidentiality) of the 5 "trust service principles" .</p>
              <p>Out of scope - Privacy & Processing Integrity.</p>
              <h3>C5</h3>
              <p>The cloud computing compliance criteria catalogue (C5) defines a baseline security level for cloud computing. It is used by professional cloud service providers, auditors and cloud customers.</p>
              <p>
                The Federal Office for Information Security in Germany (BSI Germany) initially introduced C5 in 2016. Since then, the criteria catalogue has emerged quickly and has now a broad market penetration: We know of more than a dozen C5 attestations at national, European and worldwide cloud service providers covering a broad set of cloud services. Besides large cloud service providers, by now, also medium- and small-sized providers apply the catalogue. The criteria catalogue supports
                customers in selecting, controlling and monitoring their cloud service providers. The corresponding reports build the foundation for a solid risk assessment.
              </p>
            </>
          ),
          url: 'Gen_23SOC2Type2C5.pdf',
        },
      ],
    },
    {
      id: '3',
      image: '/images/logos/compliance/Cyber-Essentials-Badge-High-Res.webp',
      title: 'Cyber Essentials',
      desc: (
        <>
          <p>Cyber Essentials is deemed the minimum requirement to provide services to the UK Public Sector and is achieved based on an assessment of both technical configuration and the application of security controls.</p>
          <p>
            Go to the IASME page and search for <strong>Genesys</strong> certificate.
          </p>
        </>
      ),
      // url: 'https://www.genesys.com/trust/cyber-essentials',
      url: 'https://iasme.co.uk/certified-organisations/',
      cta: (
        <Space className='btn'>
          <PiCertificateBold />
          Certificate Search
        </Space>
      ),
      files: [
        {
          title: 'Cyber Essentials',
          name: 'Cyber Essentials.pdf',
          desc: <p>Cyber Essentials is a United Kingdom certification scheme designed to show an organisation has a minimum level of protection in cyber security through annual assessments to maintain certification. Backed by the UK government and overseen by the National Cyber Security Centre (NCSC).</p>,
          url: 'Cyber Essentials.pdf',
        },
      ],
    },
    {
      id: '4',
      image: '/images/logos/compliance/Cyber-Essentials-Plus-Logo.webp',
      title: 'Cyber Essentials Plus',
      desc: <>Cyber Essentials Plus is a technical audit of the Genesys Cloud™ platform against the controls of the Cyber Essentials standard. The Cyber Essentials Scheme is “an effective, (UK) Government backed scheme that will help you to protect your organisation, whatever its size, against a whole range of the most common cyber attacks.”</>,
      url: 'https://www.genesys.com/trust/cyber-essentials-plus',
      files: [
        {
          title: 'Cyber Essentials Plus',
          name: 'Cyber Essentials Plus.pdf',
          desc: <p>Cyber Essentials is a United Kingdom certification scheme designed to show an organisation has a minimum level of protection in cyber security through annual assessments to maintain certification. Backed by the UK government and overseen by the National Cyber Security Centre (NCSC).</p>,
          url: 'Cyber Essentials Plus.pdf',
        },
      ],
    },
    {
      id: '5',
      image: '/images/logos/compliance/cloud-secure-1.png.webp',
      title: 'DoD Impact Level (IL) 2',
      desc: (
        <>
          The U.S. Department of Defense (DoD) Cloud Computing Security Requirements Guide (SRG) provides the baseline security requirements used to assess the security posture of a cloud service offering. Cloud Service Providers (CSPs) supporting U.S. DoD customers are required to comply with these requirements. Genesys Cloud CX has been granted a Provisional Authorization (PA) for DoD Impact Level 2 (IL2) from the Defense Information Systems Agency (DISA) leveraging Genesys’ FedRAMP Moderate
          <br />
          <br />
          Authorization. IL2 is for non-Controlled Unclassified Information (non-CUI), which includes all data cleared for public release, as well as some DoD private unclassified information not designated as CUI or critical mission data that requires some minimal level of access control.
        </>
      ),
      url: 'https://www.genesys.com/trust/dod-impact-level-il-2',
    },
    {
      id: '6',
      image: '/images/logos/compliance/ENS-Logo.webp',
      title: 'ENS',
      desc: <>The National Security Scheme (Esquema Nacional de Seguridad or ENS) applies to the entire Spanish Public Sector, as well as to suppliers that collaborate with the Administration. ENS offers a common framework of basic principles, requirements and security measures for the adequate protection of information and services. It ensures access, confidentiality, integrity, traceability, authenticity, availability and conservation of electronic data, information and services.</>,
      // url: 'https://www.genesys.com/trust/ens',
      url: 'https://gobernanza.ccn-cert.cni.es/certificados',
      cta: (
        <Space className='btn'>
          <PiCertificateBold />
          ENS Certificate List
        </Space>
      ),
      files: [
        {
          title: 'Certificado de conformidad ENS Cloud CX',
          name: 'ES_Certificado de conformidad ENS Cloud CX.pdf',
          desc: (
            <p>
              The National Security Framework, applicable to the entire Public Sector, as well as to suppliers that collaborate with the Administration, offers a common framework of basic principles, requirements and security measures for the adequate protection of the information processed and the services provided, with the aim of ensuring access, confidentiality, integrity, traceability, authenticity, availability and conservation of the data, information and services used by electronic means
              that they manage in the exercise of their competences. Since its first development in 2010, it has been in constant evolution with notable modifications in 2015 and its last update in 2022 (Royal Decree 311/2022).
            </p>
          ),
          url: 'ES_Certificado de conformidad ENS Cloud CX.pdf',
        },
      ],
    },
    {
      id: '7',
      image: '/images/logos/compliance/fedramp-logo-vert-tm.svg',
      title: 'FedRAMP',
      desc: (
        <>
          The Federal Risk and Authorization Management Program (FedRAMP®) was established in 2011 to provide a cost-effective, risk-based approach for the adoption and use of cloud services by the federal government. FedRAMP empowers agencies to use modern cloud technologies, with an emphasis on security and protection of federal information. In December 2022, the FedRAMP Authorization Act was signed as part of the FY23 National Defense Authorization Act (NDAA). The Act codifies the FedRAMP
          program as the authoritative standardized approach to security assessment and authorization for cloud computing products and services that process unclassified federal information.
        </>
      ),
      url: 'https://marketplace.fedramp.gov/products/FR2131766015',
      cta: (
        <Space className='btn'>
          <PiCertificateBold />
          See Authorization Status
        </Space>
      ),
    },
    {
      id: '8',
      image: '/images/logos/compliance/HDS-Logo.webp',
      title: 'HDS',
      desc: (
        <>
          HDS certification aims to strengthen the protection of personal health data and to build an environment of trust around eHealth and patient monitoring. It is based on reference systems including compliance with iso standards and enables certification to be issued by an accredited independent organization to any structure or organization hosting health data. Personal health data is sensitive data. Their access is regulated by law to protect the rights of individuals. The hosting of
          <br />
          <br />
          these data must therefore be carried out under security conditions adapted to their criticality. The regulations define the expected terms and conditions.
        </>
      ),
      url: 'https://www.genesys.com/trust/hds',
      files: [
        {
          title: 'HDS Certificate',
          name: 'HDS_GenericCertificate_Final.pdf',
          desc: (
            <>
              <p>HDS certification aims to strengthen the protection of personal health data and to build an environment of trust around eHealth and patient monitoring. It is based on reference systems including compliance with iso standards and enables certification to be issued by an accredited independent organization to any structure or organization hosting health data.</p>
              <p>Personal health data is sensitive data. Their access is regulated by law to protect the rights of individuals. The hosting of these data must therefore be carried out under security conditions adapted to their criticality. The regulations define the expected terms and conditions.</p>
            </>
          ),
          url: 'HDS_GenericCertificate_Final.pdf',
        },
      ],
    },
    {
      id: '9',
      image: '/images/logos/compliance/HIPAA-square-logo.webp',
      title: 'HIPAA',
      desc: <>Achieving compliance with the Health Insurance Portability & Accountability Act (HIPAA) demonstrates assurance through effectiveness of security controls that health information is secured and protected.</>,
      url: 'https://www.genesys.com/trust/hipaa',
      files: [
        {
          title: 'HIPAA Type 2 Attestation',
          name: 'GC_23 HIPAA Type 2 Attestation.pdf',
          desc: (
            <p>
              The Health Insurance Portability and Accountability Act (HIPAA) sets the standard for sensitive patient data protection. Companies that deal with protected health information (PHI) must have physical, network, and process security measures in place and follow them to ensure HIPAA Compliance. Covered entities (anyone providing treatment, payment, and operations in healthcare) and business associates (anyone who has access to patient information and provides support in treatment,
              payment, or operations) must meet HIPAA Compliance. Other entities, such as subcontractors and any other related business associates must also be in compliance.
            </p>
          ),
          url: 'GC_23 HIPAA Type 2 Attestation.pdf',
        },
      ],
    },
    {
      id: '10',
      image: '/images/logos/compliance/logo-hitrust.webp',
      title: 'HITRUST',
      desc: <>Achieving HITRUST assures internal and external stakeholders of the current state of information security and compliance, with Genesys Cloud providing even greater assurance through the attainment of the externally validated “gold standard” two-year assessment.</>,
      url: 'https://www.genesys.com/trust/hitrust',
      files: [
        {
          title: 'Genesys 2022 - HITRUST r2 Certificate',
          name: 'Genesys 2022- HITRUST r2 Cert. Ltr. (Final).pdf',
          url: 'Genesys 2022- HITRUST r2 Cert. Ltr. (Final).pdf',
        },
        {
          title: 'Genesys Cloud HITRUST r2 Interim Assessment',
          name: 'Genesys Cloud HITRUST r2 Interim Assessment.pdf',
          url: 'Genesys Cloud HITRUST r2 Interim Assessment.pdf',
        },
        {
          title: 'HITRUST Validated Assessment Letter',
          name: 'HITRUST Validated Assessment Letter.pdf',
          url: 'HITRUST Validated Assessment Letter.pdf',
        },
      ],
    },
    {
      id: '11',
      image: '/images/logos/compliance/irap-trust.webp',
      title: 'IRAP',
      desc: <>Intact Security conducted an audit (known as an assessment) as defined in the Australian Signals Directorate (ASD) Information Security Manual (ISM) and in accordance with the Genesys Cloud SOA. The ISM is developed with the principle of providing Australian Government agencies with a baseline of generic risks and controls associated with the storage and handling of security sensitive and classified information. This is designed to ensure consistency across agencies.</>,
      url: 'https://www.genesys.com/trust/irap',
      files: [
        {
          title: 'Genesys - IRAP Security Assessment Report',
          name: 'Genesys - IRAP Security Assessment Report_Final v1.1.pdf',
          desc: <p>The Information Security Registered Assessors Program (IRAP) enables Australian Government customers to validate that appropriate controls are in place and determine the appropriate responsibility model for addressing the requirements of the Australian Government Information Security Manual (ISM) produced by the Australian Cyber Security Centre (ACSC).</p>,
          url: 'Genesys - IRAP Security Assessment Report_Final v1.1.pdf',
        },
      ],
    },
    {
      id: '15',
      image: '/images/logos/compliance/pci-dss.svg',
      title: 'PCI DSS',
      desc: <>PCI DSS is the globally recognized standards for security policies, technologies and ongoing processes that protect payment systems from breaches and theft of cardholder data.</>,
      url: 'https://www.genesys.com/trust/pci-dss',
      files: [
        {
          title: 'PCI DSS Authorization',
          name: 'PCI-DSS-v4_0-AOC-Genesys_07-17-2023[86].pdf',
          desc: <p>PCI DSS is the global data security standard adopted by the payment card brands for all entities that process, store or transmit cardholder data and/or sensitive authentication data. It covers technical and operational system components included in or connected to cardholder data.</p>,
          url: 'PCI-DSS-v4_0-AOC-Genesys_07-17-2023[86].pdf',
        },
      ],
    },
    {
      id: '12',
      image: '/images/logos/compliance/logo-iso27001.webp',
      title: 'ISO 27001, ISO 27017, ISO 27018',
      desc: (
        <>
          <p>ISO27001:2013 is the globally recognized standard for an information security management system. Achieving the certification demonstrates the application of the ISMS principles, as well as the application of ISO 27002:2013 controls to secure and protect organizational data within the scope of the certification.</p>
          <p>ISO 27017:2015 extends the security controls of ISO 27002 to cloud environments. For Genesys Cloud, it’s achieved in conjunction with ISO 27001, which involves external verification that the controls are applied appropriately, and are managed and sustained.</p>
          <p>ISO 27018:2019 is the globally recognized certification extension to ISO 27001:2013. Achieving the extension certification demonstrates the application of ISO 27002:2013 controls to secure Personally Identifiable Information (PII)/privacy data in the cloud.</p>
        </>
      ),
      url: 'https://www.genesys.com/trust/iso-270012013',
      files: [
        {
          title: 'Genesys ISO 27001 Certificate Award',
          name: 'Genesys ISO 27001 Certificate Award - 6.16.2023.pdf',
          // desc: <>ISO/IEC 27001 is widely known, providing requirements for an information security management system (ISMS), though there are more than a dozen standards in the ISO/IEC 27000 family. Using them enables organizations of any kind to manage the security of assets such as financial information, intellectual property, employee details or information entrusted by third parties.</>,
          url: 'Genesys ISO 27001 Certificate Award - 6.16.2023.pdf',
        },
      ],
    },
    // {
    //   id: '13',
    //   image: '/images/logos/compliance/ISO-27017-2015.webp',
    //   title: 'ISO 27017:2015',
    //   desc: <>ISO 27017:2015 extends the security controls of ISO 27002 to cloud environments. For Genesys Cloud, it’s achieved in conjunction with ISO 27001, which involves external verification that the controls are applied appropriately, and are managed and sustained.</>,
    //   url: 'https://www.genesys.com/trust/iso-270172015',
    // },
    // {
    //   id: '14',
    //   image: '/images/logos/compliance/ISO-27018-2019.webp',
    //   title: 'ISO 27018:2019',
    //   desc: <>ISO 27018:2019 is the globally recognized certification extension to ISO 27001:2013. Achieving the extension certification demonstrates the application of ISO 27002:2013 controls to secure Personally Identifiable Information (PII)/privacy data in the cloud.</>,
    //   url: 'https://www.genesys.com/trust/iso-270182019',
    // },

    {
      id: '16',
      image: '/images/logos/compliance/logo-aicpa.webp',
      title: 'SOC 1 Type 2',
      desc: <>SOC 1 Type 2 is an independent report on the description of the Genesys Cloud platform and on the suitability of the design and operating effectiveness of its controls relevant to security, availability, and integrity, pursuant to SOC 1 Type 2 examination under ISAE 3402.</>,
      url: 'https://www.genesys.com/trust/soc-1-type-2',
      files: [
        {
          title: 'Type 2 SOC 1 Report',
          name: 'Genesys-2023-Type 2 SOC 1-Final Report.pdf',
          desc: <p>SOC (System and Organization Controls) audits are Internal Control Audit engagements that are performed for Service Organizations (organizations that provide certain functions for other entities on an outsourced basis). The SOC audit reports are typically required by and provided to the customers of the service organizations (user entities).</p>,
          url: 'Genesys-2023-Type 2 SOC 1-Final Report.pdf',
        },
      ],
    },
    {
      id: '17',
      image: '/images/logos/compliance/logo-aicpa.webp',
      title: 'SOC 2 Type 2',
      desc: <>SOC 2 Type 2 is an independent report on the description of the Genesys Cloud platform and on the suitability of the design and operating effectiveness of its controls relevant to security, availability and integrity, pursuant to SOC 2 Type 2 examination under ISAE 3000.</>,
      url: 'https://www.genesys.com/trust/soc-2-type-2',
      files: [
        {
          title: 'Type 2 SOC 2 + C5 - 2023',
          name: 'Type 2 SOC 2 + C5 - 2023.pdf',
          desc: (
            <>
              <h3>Type 2 SOC 2 With Cloud Computing Compliance Controls Catalogue (C5) & ISAE 3000 - 2023</h3>
              <p>Developed by the American Institute of CPAs (AICPA), SOC 2 defines criteria for managing customer data based on the 5 "trust service principles" . Genesys SOC scope covers only 3 (Security, Availability and Confidentiality) of the 5 "trust service principles" .</p>
              <p>Out of scope - Privacy & Processing Integrity.</p>
              <h3>C5</h3>
              <p>The cloud computing compliance criteria catalogue (C5) defines a baseline security level for cloud computing. It is used by professional cloud service providers, auditors and cloud customers.</p>
              <p>
                The Federal Office for Information Security in Germany (BSI Germany) initially introduced C5 in 2016. Since then, the criteria catalogue has emerged quickly and has now a broad market penetration: We know of more than a dozen C5 attestations at national, European and worldwide cloud service providers covering a broad set of cloud services. Besides large cloud service providers, by now, also medium- and small-sized providers apply the catalogue. The criteria catalogue supports
                customers in selecting, controlling and monitoring their cloud service providers. The corresponding reports build the foundation for a solid risk assessment.
              </p>
            </>
          ),
          url: 'Gen_23SOC2Type2C5.pdf',
        },
      ],
    },
    {
      id: '18',
      image: '/images/logos/compliance/logo-aicpa.webp',
      title: 'SOC 3 Type 2',
      desc: <>SOC 3 Type 2 is an independent report on the description of the Genesys Cloud platform and on the suitability of the design and operating effectiveness of its controls relevant to security, availability, and integrity, pursuant to SOC 3 Type 2 examination under ISAE 3402.</>,
      url: 'https://www.genesys.com/trust/soc-3-type-2',
      files: [
        {
          title: 'Type 2 SOC 3 Report',
          name: 'Genesys-GC-2023-Type 2 SOC 3-Final Report (v3).pdf',
          desc: (
            <>
              <p>Service Organization Control 3 (SOC 3) report outlines information related to a service organization’s internal controls for security, availability, processing integrity, confidentiality or privacy. These five areas are the focuses of the AICPA Trust Services Principles and Criteria.</p>
              <p>Genesys SOC scope covers only 3 (Security, Availability and Confidentiality) of the 5 "trust service principles".</p>
              <p>Out of scope - Privacy & Processing Integrity.</p>
            </>
          ),
          url: 'Genesys-GC-2023-Type 2 SOC 3-Final Report (v3).pdf',
        },
      ],
    },
    {
      id: '19',
      image: '/images/logos/compliance/Authorized-StateRAMP-Badge.webp',
      title: 'StateRAMP',
      desc: (
        <>
          Founded at the beginning of 2020, StateRAMP was born from the clear need for a standardized approach to the cybersecurity standards required from service providers offering solutions to state and local governments.
          <br />
          <br />
          StateRAMP is a registered 501(c)(6) nonprofit membership organization comprised of service providers offering IaaS, PaaS, and/or SaaS solutions, third party assessment organizations, and government officials. Our members lead, manage, and work in various disciplines across the United States and are all committed to making the digital landscape a safer, more secure place.
        </>
      ),
      url: 'https://stateramp.org/product-list/',
      cta: (
        <Space className='btn'>
          <PiCertificateBold />
          Authoized Product List
        </Space>
      ),
    },
    {
      id: '20',
      image: '/images/logos/compliance/TX-RAMP-Certified-Logo.webp',
      title: 'TX-RAMP',
      desc: <>TX-RAMP is the Texas Department of Information Resources (DIR) framework for collecting information about cloud services security posture and assessing responses for compliance with required controls and documentation. TX-RAMP requirements apply to Texas State agencies, institutions of higher education and public community colleges.</>,
      url: 'https://www.genesys.com/trust/tx-ramp-certification',
    },
  ];

  const [openModal, setOpenModal] = useState(false);
  const [selectedDocsModal, setSelectedDocsModal] = useState([]);
  //const [filter, setFilter] = useState('all');

  const handleItemOnClick = async (item) => {
    openDrawer(
      <Space className='btn'>{item.title}</Space>,
      <div className='flex flex-col gap-4'>
        <BoxType1 title={<img style={{ maxHeight: ICON_SIZE, maxWidth: '80%' }} src={item.image} alt={title} />}>
          <div className='flex flex-col gap-4'>
            <div>{item.desc}</div>
            <div>
              <Button type='primary' href={item.url} target='_blank' size='large'>
                {item.cta ?? (
                  <Space className='btn'>
                    <FaEye />
                    See more
                  </Space>
                )}
              </Button>
            </div>
          </div>
        </BoxType1>

        {item.files?.map((file, index) => (
          <BoxType1
            key={index}
            title={
              <>
                <Space className='btn'>
                  <FaFile />
                  {file.title ?? file.name}
                </Space>
              </>
            }
          >
            <div>{file.desc}</div>
            <div className='mt-4'>
              <DownloadButtonComponent
                type='primary'
                onClick={async () => {
                  try {
                    const fileUrl = await getWatermarkedFile(file.url);

                    let anchor = document.createElement('a');
                    anchor.href = fileUrl.url;
                    anchor.download = file.name;
                    anchor.target = '_self';

                    document.body.appendChild(anchor);
                    anchor.click();
                    document.body.removeChild(anchor);
                  } catch (error) {
                    notification.error(error.message);
                  }
                }}
              >
                <Space className='btn'>
                  <FaFileDownload />
                  Download
                </Space>
              </DownloadButtonComponent>
            </div>
          </BoxType1>
        ))}

        {item.files?.length > 1 && (
          <div className='mt-6'>
            <DownloadButtonComponent
              type='primary'
              onClick={async () => {
                for await (const file of item.files) {
                  const fileUrl = await getWatermarkedFile(file.url);

                  let anchor = document.createElement('a');
                  anchor.href = fileUrl.url;

                  document.body.appendChild(anchor);
                  anchor.click();
                  document.body.removeChild(anchor);
                }
              }}
            >
              <Space className='btn'>
                <FaArrowAltCircleDown />
                Download all {item.files?.length} files
              </Space>
            </DownloadButtonComponent>
          </div>
        )}
      </div>
    );
    // window.open(item.url, '_blank');
  };

  const handleBulkDownload = async () => {
    if (true === notArrayOrEmpty(selectedDocsModal)) return;
    for await (const doc of selectedDocsModal) {
      const fileUrl = await getWatermarkedFile(doc.url);

      let anchor = document.createElement('a');
      anchor.href = fileUrl.url;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
    setOpenModal(false);
  };

  const getAllFiles = () => {
    let result = [];
    itemList?.forEach((option) => {
      if (option.files) {
        result = result.concat(option.files);
      }
    });
    return result;
  };

  return (
    <>
      <Modal
        closable={false}
        onCancel={() => setOpenModal(false)}
        title={
          <div className='flex flex-row gap-1'>
            <div className='flex-auto'>
              <Space className='btn text-xl'>
                <FaArrowAltCircleDown />
                Bulk Download
              </Space>
            </div>
            <div className='flex flex-row gap-1'>
              <Tooltip title='Select all'>
                <Button type='text' onClick={() => setSelectedDocsModal(getAllFiles())}>
                  <RiCheckboxMultipleFill className='text-xl' />
                </Button>
              </Tooltip>
              <Tooltip title='Deselect all'>
                <Button type='text' onClick={() => setSelectedDocsModal([])}>
                  <RiCheckboxMultipleBlankFill className='text-xl' />
                </Button>
              </Tooltip>
              <Tooltip title='Close'>
                <Button type='text' onClick={() => setOpenModal(false)}>
                  <CloseOutlined className='text-xl' />
                </Button>
              </Tooltip>
            </div>
          </div>
        }
        open={openModal}
        footer={[
          <Button size='large' key='back' onClick={() => setOpenModal(false)}>
            <Space className='btn'>
              <CloseOutlined />
              Cancel
            </Space>
          </Button>,
          <DownloadButtonComponent size='large' className='font-bold' key='submit' type='primary' onClick={async () => await handleBulkDownload()} disabled={true === notArrayOrEmpty(selectedDocsModal)}>
            <Space className='btn'>
              <FaFileDownload />
              Download {true === notEmptyArray(selectedDocsModal) ? selectedDocsModal.length : '0'}
              {selectedDocsModal.length > 1 ? 'documents' : 'document'}
            </Space>
          </DownloadButtonComponent>,
        ]}
        width={800}
      >
        <div className='py-4 h-[60vh] overflow-auto'>
          <DocumentsCheckboxGroup options={getAllFiles()} value={selectedDocsModal} onChange={(v) => setSelectedDocsModal(v)} />
        </div>
      </Modal>
      <BoxType2
        title={title}
        extras2={
          <>
            <Button onClick={() => setOpenModal(true)}>
              <Space className='btn'>
                <FaArrowAltCircleDown />
                Bulk Download
              </Space>
            </Button>
          </>
        }
      >
        <div className='flex flex-col justify-center gap-6 my-4'>
          <div className='grid grid-cols-2 md:grid-cols-4 lg:md:grid-cols-6 gap-6 text-center'>
            {itemList.map((item, index) => (
              <ComplianceItem
                key={index}
                image={item.image}
                title={item.title}
                hasFiles={notEmptyArray(item.files)}
                onClick={() => {
                  handleItemOnClick(item);
                }}
              />
            ))}
          </div>
        </div>
      </BoxType2>
    </>
  );
};

SectionCompliance.propTypes = {};

export default SectionCompliance;
